import { isInstalledWebApp } from "./platform/shell";

/** The service worker's caches (sw/sw.js): every downloaded file, and the page with its file list. */
const AssetsCache = "highmarch-assets";
const ShellCache = "highmarch-shell";

/** The texture set this device does not load, left out of the background download (set as the download is asked for). */
let skip: RegExp | null = null;

/**
 * The files of the texture set this device does not load, as a pattern for the background download, from the build's
 * file list (`listed`). A WebP device never loads `hq/ktx2/`. A KTX2 device loads a model's or a terrain map's KTX2
 * twin where it has one (render/assets.ts, groundMaps.ts) and the WebP GLB or the PNG where it has none, so only the
 * sources of the twins that are listed are left out. (It used to leave out every loose GLB and the whole terrain folder
 * whatever had a twin: with twins for only some of the set, an iPhone never downloaded the rest, and a level its player
 * had not reached online stopped offline with "pieces of the battlefield went missing", Kevin 2026-10-25.)
 */
export function textureSetSkip(usesKtx2: boolean, listed: Iterable<string>): string {
  if (!usesKtx2) return "^hq/ktx2/";
  const sources: string[] = [];
  for (const p of listed) {
    const model = /^hq\/ktx2\/(.+)\.glb$/.exec(p);
    const map = /^hq\/ktx2\/terrain\/(.+)\.ktx2$/.exec(p);
    if (map) sources.push(`hq/terrain/${map[1]}.png`);
    else if (model) sources.push(`hq/${model[1]}.glb`);
  }
  const escaped = sources.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return escaped.length ? `^(${escaped.join("|")})$` : "^$.";
}

/** The build's file list: the worker's copy (the page's own build), else the network. */
async function fileList(): Promise<Record<string, string>> {
  const res = (await caches.open(ShellCache).then((c) => c.match("precache.json"))) ?? (await fetch("./precache.json"));
  return ((await res.json()) as { files: Record<string, string> }).files;
}

/** Whether the page re-asks for the download each time it comes back to the front (see requestWholeGame). */
let reasking = false;

/**
 * Asks the offline cache to download the rest of the game in the background (sw/sw.js `warm`) — only in the installed
 * web app: a Safari tab loads each level's files as it is reached. The texture set this device does not load is left
 * out (textureSetSkip). iOS stops the worker, and its download with it, when the app goes to the background, so the
 * page asks again whenever it is back in front; the worker runs one download at a time and skips what it holds.
 */
export async function requestWholeGame(reg: ServiceWorkerRegistration | null, usesKtx2: boolean): Promise<void> {
  try {
    const pattern = textureSetSkip(usesKtx2, typeof caches === "undefined" ? [] : Object.keys(await fileList()));
    skip = new RegExp(pattern);
    if (!isInstalledWebApp() || !reg) return;
    const ask = () => reg.active?.postMessage({ warm: true, skip: pattern });
    ask();
    if (reasking || typeof document === "undefined") return;
    reasking = true;
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") ask(); });
  } catch {
    /* no file list yet (offline before the first one): the next launch asks */
  }
}

/** How much of the game is on this device: files cached of the files this device needs, and whether it is installed. */
export interface OfflineState { readonly installed: boolean; readonly cached: number; readonly total: number }

export async function offlineState(): Promise<OfflineState | null> {
  try {
    if (typeof caches === "undefined") return null;
    const files = await fileList();
    const base = new URL("./", location.href).href;
    const have = new Set((await (await caches.open(AssetsCache)).keys()).map((r) => r.url));
    const wanted = Object.entries(files).filter(([p]) => !skip?.test(p));
    const cached = wanted.filter(([p, h]) => have.has(new URL(`${p}?v=${h}`, base).href)).length;
    return { installed: isInstalledWebApp(), cached, total: wanted.length };
  } catch {
    return null;
  }
}

/** The Settings line for it (minimal copy: docs/VOICE.md). */
export function offlineStateText(s: OfflineState | null): string {
  if (!s) return "Unavailable here";
  if (s.cached >= s.total) return "Ready";
  const pct = `${Math.floor((100 * s.cached) / Math.max(1, s.total))}%`;
  return s.installed ? `Downloading · ${pct}` : `Add to Home Screen · ${pct}`;
}
